/*
 * -----------------------------------------------------------------------
 *  <copyright file="display-by-category.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import React from 'react';

import {
    FormatNumberForChart,
    HC_COLORS_STANDARD,
    IOpraChartHorizontalLine,
    OpraChartLineStyle,
    OpraColours,
    OpraVerticalAlign,
} from '../../../../../interfaces/opra-chart.interface';
import LineByCategoryChart from '../../../core/charts/line-by-category-chart';
import PanelConstants from '../add-panel/panel-constants';

const getLineByCategory = ({
    getRef,
    panelState,
    study,
    siteId,
    sites,
    subjects,
    setLoadingComplete,
    previewMode,
}: any) => {
    if (!panelState?.panelData?.panelData?.length) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }
    const getHorizontalLines = () => {
        switch (panelState.chartConfig.seriesType) {
            case PanelConstants.seriesType.SUBJECT_BY_VISIT:
            case PanelConstants.seriesType.SUBJECTBYVISIT:
                if (
                    panelState.panelData.panelData[0].threshold1 >= 0 &&
                    panelState.panelData.panelData[0].threshold2 >= 0
                ) {
                    return [
                        {
                            name: '',
                            color: HC_COLORS_STANDARD[OpraColours.Red],
                            style: OpraChartLineStyle.Dashed,
                            y: panelState.panelData.panelData[0].threshold1,
                        },
                        {
                            name: '',
                            color: HC_COLORS_STANDARD[OpraColours.Red],
                            style: OpraChartLineStyle.Dashed,
                            y: panelState.panelData.panelData[0].threshold2,
                        },
                    ] as IOpraChartHorizontalLine[];
                }
                return [];
            default:
                return [];
        }
    };
    const categories: string[] = Array.from(
        new Set(panelState.panelData.panelData[0].lineSubjectByVisitCategories ?? []),
    );
    const horizontalLines = getHorizontalLines();
    const sbjData =
        siteId && panelState.panelData.panelData[0].lineSubjectByVisitForSiteIds
            ? panelState.panelData.panelData[0].lineSubjectByVisitForSiteIds[siteId]
            : panelState.panelData.panelData[0].lineSubjectByVisitForStudy;
    const secondKey = previewMode
        ? undefined
        : `Lower Threshold: ${FormatNumberForChart(
              panelState.panelData.panelData[0].threshold1,
          )}   Upper Threshold: ${FormatNumberForChart(panelState.panelData.panelData[0].threshold2)} `;
    const ListOfSubjects = Object.keys(sbjData).map((subjectId: string): any => ({
        subjectName: subjects[subjectId]?.Fields['Subject.subjectNumber'] ?? 'Unknown subject code',
        visits: sbjData[subjectId].visits,
    }));

    const seriesData =
        !siteId || !panelState.chartConfig?.hideStudyColumns
            ? [
                  {
                      name: `${study?.studyNumber ?? 'Study'} (All Sites)`,
                      values: ListOfSubjects,
                  },
              ]
            : [];

    if (siteId && panelState.panelData.panelData[0].lineSubjectByVisitForSiteIds)
        seriesData.push({
            name: `Site: ${sites[siteId]?.Fields['Site.name']}`,
            values: ListOfSubjects,
        });

    return (
        <LineByCategoryChart
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            categories={categories}
            yAxisTitle={panelState.chartConfig.yTitle}
            xAxisTitle={panelState.chartConfig.xTitle}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            series={seriesData.reverse()}
            horizontalLines={horizontalLines}
            secondaryKey={{
                text: secondKey,
                position: OpraVerticalAlign.bottom,
            }}
            hidePlotTitle={panelState.hidePlotTitle}
        />
    );
};

export { getLineByCategory };
